import { useCallback, useState } from 'react'
import GlobalConfirmationModal, {
  ModalTypes,
} from '../../GlobalConfirmationModal/GlobalConfirmationModal'
import {
  BlockedMonitoringPeriodDetails,
  getBlockedMonitoringPeriodDetails,
} from '../../../utils/blockedPeriodError'
import { useCustomSnackbarError } from '../../../utils/Snackbar/useCustomSnackbarError'

const buildBlockedMessage = ({
  carbonInstances,
  blockedMonitoringPeriods,
}: BlockedMonitoringPeriodDetails): string => {
  const details = [
    carbonInstances.length
      ? `Instancias: ${carbonInstances.map(({ name }) => name).join(', ')}.`
      : null,
    blockedMonitoringPeriods.length ? `Períodos: ${blockedMonitoringPeriods.join(', ')}.` : null,
  ]
    .filter(Boolean)
    .join(' ')

  const reason =
    'No se guardaron los cambios porque el área de exclusión se superpone con instancias de carbono de períodos de monitoreo bloqueados.'

  return details ? `${reason} ${details}` : reason
}

// Exclusion-area writes are validated on save, so a blocked-period rejection is a normal outcome
// rather than a failure: it gets a modal an operator has to acknowledge, because the transient
// snackbar is too easy to miss while deciding what to redraw.
//
// The dialog is owned locally instead of through AppContext's confirmationModalConfig: a rejection
// can surface inside another dialog's confirmAction, and GlobalConfirmationModal calls handleClose
// as soon as that promise settles, replacing the whole shared config before this one can be seen.
export const useExclusionAreaWriteError = () => {
  const { handleError } = useCustomSnackbarError()
  const [blocked, setBlocked] = useState<BlockedMonitoringPeriodDetails | null>(null)

  const handleWriteError = useCallback(
    (error: unknown) => {
      const details = getBlockedMonitoringPeriodDetails(error)

      if (!details) {
        handleError(error)
        return
      }

      setBlocked(details)
    },
    [handleError],
  )

  const dismiss = useCallback(() => setBlocked(null), [])

  const blockedPeriodDialog = blocked ? (
    <GlobalConfirmationModal
      buttonValue={null}
      open
      cancelLabel="Cerrar"
      confirmLabel="Entendido"
      detail=""
      error=""
      handleClose={dismiss}
      handleConfirm={dismiss}
      message={buildBlockedMessage(blocked)}
      modalType={ModalTypes.QuestionModalType}
      title="Edición bloqueada"
    >
      {null}
    </GlobalConfirmationModal>
  ) : null

  return { handleWriteError, blockedPeriodDialog }
}
